/**
 * Coupon API Requests
 *
 * Hooks for coupon-related API calls.
 * Business logic is 100% identical to mobile module-flash-sale.
 *
 * @module @kidslabs/module-flash-sale/api/requests/coupon
 */

import {
    type AddCouponToMyListMutationVariables,
    type CouponContentResponse,
    CouponWidgetContentDocument,
    type CouponWidgetContentQueryVariables,
    useAddCouponToMyListMutation,
    useCouponWidgetContentQuery,
    useGetAppliedUniversalCodesLazyQuery,
    useGetAppliedUniversalCodesQuery,
} from '../../types/front.graphql';

/**
 * Hook for storing coupon to customer wallet
 *
 * Adds a coupon to the customer's wallet and refetches coupon widget content.
 *
 * Business logic is 100% identical to mobile:
 * - Uses no-cache fetch policy
 * - Refetches CouponWidgetContent after mutation
 * - Returns undefined on error
 */
export const useStoreCouponToWallet = () => {
    const [storeCouponToWallet, { data, loading, error }] = useAddCouponToMyListMutation();

    const addCouponToWallet = async (variables: AddCouponToMyListMutationVariables) => {
        try {
            const response = await storeCouponToWallet({
                fetchPolicy: 'no-cache',
                variables,
                refetchQueries: [CouponWidgetContentDocument],
                awaitRefetchQueries: true,
            });

            return response?.data;
        } catch {
            return undefined;
        }
    };

    return {
        addCouponToWallet,
        status: data?.addCouponToMyList,
        loading,
        error,
    };
};

/**
 * Hook for fetching coupon widget content
 *
 * Retrieves coupon content for display in widgets.
 *
 * @param variables - Filter variables for coupon query
 * @param skip - Whether to skip the query
 */
export const useCouponWidgetContent = (variables?: CouponWidgetContentQueryVariables, skip?: boolean) => {
    const { data, loading, error, refetch, networkStatus } = useCouponWidgetContentQuery({
        fetchPolicy: 'cache-and-network',
        variables,
        skip: !variables || skip,
        notifyOnNetworkStatusChange: true,
    });

    const couponContent = (data?.couponWidgetContent as CouponContentResponse[]) ?? null;

    return {
        couponContent,
        loading,
        error,
        refetch,
        networkStatus,
    };
};

/**
 * Hook for getting applied coupons on cart
 *
 * @param variables - Cart ID variables
 */
export const useGetAppliedCoupons = (variables?: { cartId: string }) => {
    const { data, loading, error, refetch } = useGetAppliedUniversalCodesQuery({
        fetchPolicy: 'no-cache',
        variables,
        skip: !variables,
    });
    return {
        appliedCoupons: data?.cart?.applied_universal_codes ?? [],
        loading,
        error,
        refetch,
    };
};

/**
 * Hook for lazily fetching applied coupons
 *
 * Provides a function to manually trigger the coupon fetch.
 */
export const useGetAppliedCouponsLazy = () => {
    const [fetchCoupons, { data, loading, error, refetch }] = useGetAppliedUniversalCodesLazyQuery({
        fetchPolicy: 'no-cache',
    });

    const appliedCoupons = data?.cart?.applied_universal_codes ?? [];
    const discountAmounts = data?.cart?.prices?.discounts;

    return {
        fetchCoupons,
        refetch,
        discountAmounts,
        appliedCoupons,
        loading,
        error,
    };
};
